import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, SortDesc } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { ProductCard } from '@/components/products/ProductCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useProducts } from '@/hooks/useApi.js';
import { cn } from '@/lib/utils';

// Categories matching mobile app
const CATEGORIES = [
  { label: 'Home', value: 'All' },
  { label: 'Boys', value: 'Boys' },
  { label: 'Girls', value: 'Girls' },
  { label: 'Ladies', value: 'Ladies' },
  { label: 'Men', value: 'Men' },
];

// Sub-categories matching mobile app exactly
const SUB_CATEGORIES = {
  Boys: [
    { key: 'All', label: 'All' },
    { key: 'T-Shirts', label: 'T-Shirts' },
    { key: 'Shirts', label: 'Shirts' },
    { key: 'Jeans', label: 'Jeans' },
    { key: 'Shorts', label: 'Shorts' },
    { key: 'Pants', label: 'Pants' },
  ],
  Girls: [
    { key: 'All', label: 'All' },
    { key: 'Dresses', label: 'Dresses' },
    { key: 'Frocks', label: 'Frocks' },
    { key: 'Night Dress', label: 'Night Dress' },
    { key: 'Skirts', label: 'Skirts' },
  ],
  Ladies: [
    { key: 'All', label: 'All' },
    { key: 'Kurtis', label: 'Kurtis' },
    { key: 'Sarees', label: 'Sarees' },
    { key: 'Short Kurtis', label: 'Short Kurtis' },
    { key: 'T-Shirts', label: 'T-Shirts' },
    { key: 'Jeans', label: 'Jeans' },
    { key: 'Leggings', label: 'Leggings' },
    { key: 'Nighty', label: 'Nighty' },
  ],
  Men: [
    { key: 'All', label: 'All' },
    { key: 'T-Shirts', label: 'T-Shirts' },
    { key: 'Shirts', label: 'Shirts' },
    { key: 'Pants', label: 'Pants' },
    { key: 'Jeans', label: 'Jeans' },
    { key: 'Shorts', label: 'Shorts' },
    { key: 'Tracks', label: 'Tracks' },
    { key: 'Night Pants', label: 'Night Pants' },
  ],
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'popularity', label: 'Popularity' },
];

const AllProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL params or defaults
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'All'
  );
  const [selectedSubCategory, setSelectedSubCategory] = useState(
    searchParams.get('subCategory') || 'All'
  );
  const [sortBy, setSortBy] = useState(
    searchParams.get('sort') || 'popularity'
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get('page') || '0', 10)
  );

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory !== 'All') params.set('category', selectedCategory);
    if (selectedSubCategory !== 'All') params.set('subCategory', selectedSubCategory);
    if (sortBy !== 'popularity') params.set('sort', sortBy);
    if (page > 0) params.set('page', page.toString());
    
    setSearchParams(params, { replace: true });
  }, [selectedCategory, selectedSubCategory, sortBy, page, setSearchParams]);

  // Get sub-categories for current category
  const subCategories = useMemo(() => {
    if (selectedCategory === 'All') return [];
    return SUB_CATEGORIES[selectedCategory as keyof typeof SUB_CATEGORIES] || [];
  }, [selectedCategory]);

  // Prepare API parameters
  const apiParams = useMemo(() => ({
    category: selectedCategory === 'All' ? undefined : selectedCategory,
    subCategory: selectedSubCategory === 'All' ? undefined : selectedSubCategory,
    sort: sortBy,
    page,
    size: 20,
  }), [selectedCategory, selectedSubCategory, sortBy, page]);

  // Fetch products
  const { data: productsData, isLoading, error, refetch } = useProducts(apiParams);
  
  const products = productsData?.content || [];
  const totalPages = productsData?.page?.totalPages || 0;
  const totalElements = productsData?.page?.totalElements || 0;
  const hasMore = !productsData?.page?.last;

  // Reset page when category/subcategory changes
  useEffect(() => {
    if (page > 0) {
      setPage(0);
    }
  }, [selectedCategory, selectedSubCategory, sortBy]);

  // Handle category selection
  const handleCategorySelect = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubCategory('All'); // Reset subcategory when changing main category
    setPage(0);
  };

  // Handle subcategory selection
  const handleSubCategorySelect = (value: string) => {
    setSelectedSubCategory(value);
    setPage(0);
  };

  // Handle sort change
  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(0);
  };

  // Get category theme color (simple approach)
  const getCategoryColor = (category: string) => {
    const colors = {
      'Boys': 'bg-blue-500 border-blue-500',
      'Girls': 'bg-pink-500 border-pink-500', 
      'Ladies': 'bg-purple-500 border-purple-500',
      'Men': 'bg-gray-800 border-gray-800',
      'All': 'bg-primary border-primary'
    };
    return colors[category as keyof typeof colors] || colors['All'];
  };

  const categoryColorClass = getCategoryColor(selectedCategory);

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Error loading products</h1>
          <p className="text-muted-foreground mb-4">Something went wrong while fetching products.</p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">All Products</h1>
          <p className="text-muted-foreground">
            {isLoading ? 'Loading...' : `${totalElements} products found`}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((category) => {
              const isActive = selectedCategory === category.value;
              return (
                <button
                  key={category.value}
                  onClick={() => handleCategorySelect(category.value)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                    'border border-gray-200 hover:border-primary',
                    isActive 
                      ? `text-white ${categoryColorClass}` 
                      : 'text-gray-600 bg-white hover:text-primary hover:bg-gray-50'
                  )}
                >
                  {category.label}
                </button>
              );
            })}
          </div>

          {/* Sub-categories */}
          {subCategories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {subCategories.map((subCat) => {
                const isActive = selectedSubCategory === subCat.key;
                return (
                  <Badge
                    key={subCat.key}
                    variant={isActive ? "default" : "outline"}
                    className={cn(
                      'cursor-pointer px-3 py-1 text-xs hover:bg-primary hover:text-primary-foreground transition-colors',
                      isActive && 'bg-primary text-primary-foreground'
                    )}
                    onClick={() => handleSubCategorySelect(subCat.key)}
                  >
                    {subCat.label}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>

        {/* Sort and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span>Showing results for: </span>
            <Badge variant="secondary" className="text-xs">
              {selectedCategory === 'All' ? 'All Categories' : selectedCategory}
              {selectedSubCategory !== 'All' && ` > ${selectedSubCategory}`}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <SortDesc className="h-4 w-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[3/4] rounded-lg" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-6 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Products Grid */}
        {!isLoading && products.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              {products.map((product, index) => (
                <motion.div
                  key={product.productId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page === totalPages - 1}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && products.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
              <Filter className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No products found</h2>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or browse different categories.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedCategory('All');
                  setSelectedSubCategory('All');
                  setSortBy('popularity');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
              <Button onClick={() => navigate('/')}>
                Go Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllProductsPage;